import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeProvider';
import { LanguageProvider, useTranslation } from './i18n/LanguageProvider';
import PublicRoute from './routes/PublicRoute';
import ProtectedRoute from './routes/ProtectedRoute';
import AdminRoute from './routes/AdminRoute';
import RoleRoute from './routes/RoleRoute';
import OrganizationAccessGate from './components/auth/OrganizationAccessGate';
import AppShell from './components/layout/AppShell';
import CaseFlowLogo from './components/layout/CaseFlowLogo';
import ErrorBoundary from './components/system/ErrorBoundary';
import PageCrashFallback from './components/system/PageCrashFallback';
import SystemNoticeBanner from './components/system/SystemNoticeBanner';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));
const DebugAuthPage = lazy(() => import("./pages/DebugAuthPage.jsx"));
// Existing pages from the project
const TrustCenterPage = lazy(() => import('./pages/TrustCenterPage'));

// Public Marketing Pages (HY3 - English marketing site)
const HomePage = lazy(() => import('./pages/public/HomePage'));

const isPilotEnvironment =
  import.meta.env.MODE === 'pilot' ||
  import.meta.env.VITE_ENVIRONMENT === 'pilot' ||
  import.meta.env.VITE_APP_ENVIRONMENT === 'pilot' ||
  import.meta.env.VITE_APP_ENV === 'pilot';
const ProductPage = lazy(() => import('./pages/public/ProductPage'));
const PricingPage = lazy(() => import('./pages/public/PricingPage'));
const FAQPage = lazy(() => import('./pages/public/FAQPage'));
const AboutPage = lazy(() => import('./pages/public/AboutPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const SystemHealthPage = lazy(() => import('./pages/SystemHealthPage'));
const TenantAdminPage = lazy(() => import('./pages/TenantAdminPage'));
const PlatformGovernancePage = lazy(() => import('./pages/PlatformGovernancePage'));
const EnterpriseCommandCenter = lazy(() => import('./pages/EnterpriseCommandCenter'));
const EnterpriseEnablementPage = lazy(() => import('./pages/EnterpriseEnablementPage'));

// Operations pages (Phase 8B)
const SecondLineDashboardPage = lazy(() => import('./pages/operations/SecondLineDashboardPage'));
const TeamQueuePage = lazy(() => import('./pages/operations/TeamQueuePage'));
const ManagerDashboardPage = lazy(() => import('./pages/operations/ManagerDashboardPage'));
const WorkforceAnalyticsPage = lazy(() => import('./pages/operations/WorkforceAnalyticsPage'));
const TrendReportingPage = lazy(() => import('./pages/operations/TrendReportingPage'));

// Placeholder pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'));
const AIPage = lazy(() => import('./pages/AIPage'));

// Settings pages
const UserManagementPage = lazy(() => import('./pages/settings/UserManagementPage'));
const AcceptInvitationPage = lazy(() => import('./pages/settings/AcceptInvitationPage'));

// Loading fallback with translation support
const PageLoader = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="mb-6">
          <CaseFlowLogo heightClass="h-12" className="mx-auto" />
        </div>
        <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading') || 'Laddar...'}</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary fallback={({ resetErrorBoundary }) => <PageCrashFallback resetErrorBoundary={resetErrorBoundary} />}>
      <LanguageProvider>
        <ThemeProvider>
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route 
                  path="/login" 
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  } 
                />
                
                {/* Public Marketing Routes (HY3 - English marketing site) */}
                <Route path="/" element={isPilotEnvironment ? <Navigate to="/login" replace /> : <HomePage />} />
                <Route path="/product" element={<ProductPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />

                {/* Trust Center - Can be public if no internal data shown */}
                <Route
                  path="/trust"
                  element={
                    <PublicRoute>
                      <TrustCenterPage />
                    </PublicRoute>
                  }
                />

                {/* Unauthorized Page */}
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                {/* Debug Page - Only in DEV/DEMO mode */}
                <Route path="/debug/auth" element={<DebugAuthPage />} />
                {/* Protected App Routes with AppShell Layout */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <OrganizationAccessGate>
                        <AppShell>
                          <Routes>
                            <Route path="/" element={<DashboardPage />} />
                            <Route path="dashboard" element={<DashboardPage />} />
                            <Route path="customers" element={<CustomersPage />} />
                            <Route path="customers/:customerId" element={<CustomersPage />} />
                            <Route path="workspace" element={<WorkspacePage />} />
                            <Route path="ai" element={<AIPage />} />
                            
                            {/* Operations Routes (Phase 8B) */}
                            <Route 
                              path="operations/second-line" 
                              element={<SecondLineDashboardPage />} 
                            />
                            <Route 
                              path="operations/team-queue" 
                              element={<TeamQueuePage />} 
                            />
                            <Route 
                              path="operations/manager" 
                              element={<ManagerDashboardPage />} 
                            />
                            <Route 
                              path="operations/workforce" 
                              element={<WorkforceAnalyticsPage />} 
                            />
                            <Route
                              path="operations/trends"
                              element={<TrendReportingPage />}
                            />
                            
                            {/* Settings Routes */}
                            <Route
                              path="settings/users"
                              element={
                                <RoleRoute allowedRoles={['owner', 'admin']}>
                                  <UserManagementPage />
                                </RoleRoute>
                              }
                            />
                            
                            {/* Accept Invitation - Public route */}
                            <Route
                              path="invite/:token"
                              element={<AcceptInvitationPage />}
                            />
                            
                            {/* System Health - Admin only */}
                            <Route 
                              path="system-health" 
                              element={
                                <AdminRoute>
                                  <SystemHealthPage />
                                </AdminRoute>
                              } 
                            />
                            
                            {/* Tenant Admin - Admin only */}
                            <Route 
                              path="admin/tenant" 
                              element={
                                <AdminRoute>
                                  <TenantAdminPage />
                                </AdminRoute>
                              } 
                            />
                            
                            {/* Platform Governance - Admin only */}
                            <Route 
                              path="platform-governance" 
                              element={
                                <AdminRoute>
                                  <PlatformGovernancePage />
                                </AdminRoute>
                              } 
                            />
                            
                            {/* Enterprise Command - Admin only */}
                            <Route 
                              path="enterprise" 
                              element={
                                <AdminRoute>
                                  <EnterpriseCommandCenter />
                                </AdminRoute>
                              } 
                            />
                            
                            {/* Enterprise Enablement - Admin only */}
                            <Route 
                              path="enterprise-command" 
                              element={
                                <AdminRoute>
                                  <EnterpriseEnablementPage />
                                </AdminRoute>
                              } 
                            />
                            
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                          </Routes>
                        </AppShell>
                      </OrganizationAccessGate>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
